import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';
import {
  AlertCircle,
  Download,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';
import usePnl from '../hooks/usePnl';
import './ReportsTab.css';

const MODES = [
  { id: 'month',   label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year',    label: 'Year' },
  { id: 'custom',  label: 'Custom' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const QUARTER_MONTHS = {
  1: [0, 1, 2],
  2: [3, 4, 5],
  3: [6, 7, 8],
  4: [9, 10, 11],
};

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
});
const fmtMoney = (n) => NAIRA.format(Number(n || 0));

/* ASCII-only money formatter for PDF use. jsPDF's bundled Helvetica
   doesn't include the ₦ glyph or the U+2212 minus, and when it hits
   an unsupported codepoint the whole string's character spacing
   breaks (digits render with gaps). We render plain comma-separated
   numbers in the PDF and label the column header "Amount (NGN)". */
const PDF_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const fmtPdfAmount = (n, { negative = false } = {}) => {
  const v = Math.abs(Number(n || 0));
  const sign = negative && v > 0 ? '-' : '';
  return sign + PDF_NUMBER.format(v);
};
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

/* ─── Period helpers (pure, module scope) ──────────────────────── */

function toIsoDate(d) {
  // Local date → YYYY-MM-DD (no UTC conversion that could land on the
  // previous/next day depending on the user's timezone).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function endOfDay(iso) {
  // Backend resolves ranges inclusively at midnight, so we send end-of-day
  // for the `to` boundary to capture the entire selected day/month/etc.
  const d = new Date(iso);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function monthRange(year, monthIdx) {
  const from = new Date(year, monthIdx, 1);
  const to   = new Date(year, monthIdx + 1, 0); // last day of month
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function quarterRange(year, quarter) {
  const months = QUARTER_MONTHS[quarter];
  const from = new Date(year, months[0], 1);
  const to   = new Date(year, months[2] + 1, 0);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function yearRange(year) {
  const from = new Date(year, 0, 1);
  const to   = new Date(year, 11, 31);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function labelForPeriod({ mode, year, month, quarter, customFrom, customTo }) {
  if (mode === 'month')   return `${MONTH_NAMES[month]} ${year}`;
  if (mode === 'quarter') return `Q${quarter} ${year}`;
  if (mode === 'year')    return `${year}`;
  if (mode === 'custom' && customFrom && customTo) {
    return `${new Date(customFrom).toDateString()} — ${new Date(customTo).toDateString()}`;
  }
  return 'Custom range';
}

/* ─── Sub-components ────────────────────────────────────────────── */

function ModeSwitcher({ value, onChange }) {
  return (
    <div className="fin-rep-modes" role="radiogroup" aria-label="Report period type">
      {MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          role="radio"
          aria-checked={value === m.id}
          className={`fin-rep-mode-pill${value === m.id ? ' is-active' : ''}`}
          onClick={() => onChange(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

/* P&L row — variants control visual weight. */
function PnlRow({ label, amount, sub, variant = 'line' }) {
  return (
    <div className={`fin-rep-pnl-row fin-rep-pnl-row--${variant}`}>
      <div className="fin-rep-pnl-row-label">
        <span>{label}</span>
        {sub && <span className="fin-rep-pnl-row-sub">{sub}</span>}
      </div>
      <span className="fin-rep-pnl-row-amount">
        {variant === 'cogs' || variant === 'expense' ? '−' : ''}{fmtMoney(amount)}
      </span>
    </div>
  );
}

function PnlSectionHead({ children }) {
  return <h3 className="fin-rep-pnl-section">{children}</h3>;
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

function PnlSkeleton() {
  return (
    <div className="fin-rep-skel">
      {[0,1,2,3,4,5,6,7].map((i) => (
        <div key={i} className="fin-rep-skel-row" />
      ))}
    </div>
  );
}

/* ─── PDF builder (module scope so tests / reuse stays easy) ─────
   ASCII-only by design — see fmtPdfAmount note above. The bundled
   jsPDF Helvetica can't render the Naira glyph cleanly, so amounts
   are plain numbers and the column header reads "Amount (NGN)". */

function buildPnlPdf({ store, periodLabel, kpis, passThrough, expensesByCategory, otherIncomeByCategory }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const contentW = pageW - marginX * 2;
  const labelX = marginX;
  const amountX = marginX + contentW; // right-aligned column edge

  // Colour palette (tuple form so jsPDF doesn't choke on hex)
  const C = {
    ink:    [15, 23, 42],
    body:   [55, 65, 81],
    muted:  [107, 114, 128],
    soft:   [156, 163, 175],
    rule:   [229, 231, 235],
    brand:  [13, 116, 99],
    brandSoft: [232, 246, 244], // 6% teal background tint
    accent: [180, 83, 9],       // expense / cogs amber
  };
  const setFill   = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setStroke = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const setText   = (c) => doc.setTextColor(c[0], c[1], c[2]);

  // ─── Header zone ─────────────────────────────────────────────
  let y = 56;

  // Store name (left)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setText(C.ink);
  doc.text(store?.name || 'Profit & Loss', labelX, y);

  // Statement label (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setText(C.brand);
  doc.text('PROFIT & LOSS STATEMENT', amountX, y, { align: 'right' });

  y += 16;

  // Store sub-info (email · phone · web) — left
  const subParts = [
    store?.email,
    store?.phone,
    store?.customDomain
      ? store.customDomain
      : (store?.slug ? `${store.slug}.userevstar.com` : null),
  ].filter(Boolean);
  if (subParts.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setText(C.muted);
    doc.text(subParts.join('  ·  '), labelX, y);
  }

  // Period (right)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setText(C.muted);
  doc.text(`Period: ${periodLabel}`, amountX, y, { align: 'right' });

  y += 8;

  // Address line (left, optional second row)
  if (store?.address) {
    y += 12;
    doc.setFontSize(10);
    setText(C.muted);
    doc.text(String(store.address), labelX, y);
  }

  y += 18;

  // Divider
  setStroke(C.rule);
  doc.setLineWidth(0.8);
  doc.line(labelX, y, amountX, y);
  y += 22;

  // ─── P&L body ────────────────────────────────────────────────

  // Column headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setText(C.soft);
  doc.text('DESCRIPTION', labelX, y);
  doc.text('AMOUNT (NGN)', amountX, y, { align: 'right' });
  y += 12;
  setStroke(C.rule);
  doc.setLineWidth(0.4);
  doc.line(labelX, y, amountX, y);
  y += 16;

  // Row writer
  const writeSection = (label) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setText(C.brand);
    doc.text(String(label).toUpperCase(), labelX, y);
    y += 14;
  };
  const writeLine = (label, amount, { indent = 0, muted = false, negative = false } = {}) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    setText(muted ? C.muted : C.body);
    doc.text(String(label), labelX + indent, y);
    if (amount != null) {
      doc.text(fmtPdfAmount(amount, { negative }), amountX, y, { align: 'right' });
    }
    y += 16;
  };
  const writeTotal = (label, amount, { negative = false } = {}) => {
    y += 2;
    setStroke(C.rule);
    doc.setLineWidth(0.4);
    doc.line(labelX, y - 4, amountX, y - 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setText(C.ink);
    doc.text(String(label), labelX, y + 8);
    doc.text(fmtPdfAmount(amount, { negative }), amountX, y + 8, { align: 'right' });
    y += 20;
  };
  const gap = (n = 6) => { y += n; };

  // INCOME
  writeSection('Income');
  writeLine('Sales revenue', kpis.revenue, { indent: 14 });
  if (kpis.otherIncome > 0) {
    writeLine('Other income', kpis.otherIncome, { indent: 14 });
    for (const c of otherIncomeByCategory) {
      writeLine(c.category.replace(/_/g, ' '), c.amount, { indent: 28, muted: true });
    }
  }
  writeTotal('Total income', kpis.totalIncome);
  gap();

  // PASS-THROUGH (only if any present)
  if (passThrough && passThrough.total > 0) {
    writeSection('Pass-through (not your revenue)');
    if (passThrough.tax > 0) {
      writeLine('Tax collected', passThrough.tax, { indent: 14 });
    }
    if (passThrough.deliveryFees > 0) {
      writeLine('Delivery fees collected', passThrough.deliveryFees, { indent: 14 });
    }
    if (passThrough.serviceChargeCustomerPaid > 0) {
      writeLine('Service charge (customer-paid)', passThrough.serviceChargeCustomerPaid, { indent: 14 });
    }
    if (passThrough.tipAmount > 0) {
      writeLine('Tips', passThrough.tipAmount, { indent: 14 });
    }
    writeTotal('Total pass-through', passThrough.total);
    gap();
  }

  // COGS
  writeSection('Cost of goods sold');
  writeLine('Cost of goods sold', kpis.cogs, { indent: 14, negative: true });
  gap(2);

  // Gross profit
  writeTotal('Gross profit', kpis.grossProfit);
  gap();

  // EXPENSES
  writeSection('Operating expenses');
  if (expensesByCategory.length === 0) {
    writeLine('No expenses logged in this period', null, { indent: 14, muted: true });
  } else {
    for (const c of expensesByCategory) {
      const label = c.category === 'PLATFORM_FEES'
        ? 'Platform service charge'
        : c.category.replace(/_/g, ' ');
      writeLine(label, c.amount, { indent: 14, negative: true });
    }
  }
  writeTotal('Total expenses', kpis.expenses, { negative: true });
  gap(10);

  // ─── Net profit highlight ────────────────────────────────────
  const npHeight = 56;
  setFill(C.brandSoft);
  doc.rect(labelX, y, contentW, npHeight, 'F');
  // brand-coloured left accent
  setFill(C.brand);
  doc.rect(labelX, y, 4, npHeight, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setText(C.brand);
  doc.text('NET PROFIT', labelX + 16, y + 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setText(C.muted);
  doc.text(
    `Net margin ${fmtPct(kpis.netMargin)}  ·  Gross margin ${fmtPct(kpis.grossMargin)}`,
    labelX + 16,
    y + 40
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  setText(C.brand);
  doc.text(fmtPdfAmount(kpis.netProfit, { negative: kpis.netProfit < 0 }), amountX - 14, y + 32, { align: 'right' });

  y += npHeight + 24;

  // ─── Footer ──────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setText(C.soft);
  const generated = `Generated by Revstar Finance on ${new Date().toLocaleDateString('en-NG', {
    year: 'numeric', month: 'long', day: 'numeric',
  })}`;
  doc.text(generated, labelX, pageH - 32);
  doc.text('All amounts shown in NGN', amountX, pageH - 32, { align: 'right' });

  return doc;
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function ReportsTab() {
  const toast = useToast();
  const store = useSelector((s) => s.store?.current);

  const now = new Date();
  const [mode, setMode]       = useState('month');
  const [year, setYear]       = useState(now.getFullYear());
  const [month, setMonth]     = useState(now.getMonth());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [customFrom, setCustomFrom] = useState(toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [customTo, setCustomTo]     = useState(toIsoDate(now));

  // Resolve the active from/to range based on current mode.
  const range = useMemo(() => {
    if (mode === 'month')   return monthRange(year, month);
    if (mode === 'quarter') return quarterRange(year, quarter);
    if (mode === 'year')    return yearRange(year);
    return { from: customFrom, to: customTo };
  }, [mode, year, month, quarter, customFrom, customTo]);

  // End-of-day on `to` so the backend includes the full final day.
  const fetchTo = useMemo(() => range.to ? endOfDay(range.to) : null, [range.to]);
  const { data, loading, error, refetch } = usePnl({ from: range.from, to: fetchTo });

  const kpis = data?.kpis || {
    revenue: 0, cogs: 0, grossProfit: 0, expenses: 0,
    otherIncome: 0, totalIncome: 0, netProfit: 0, grossMargin: 0, netMargin: 0,
    grossCustomerPaid: 0, discountTotal: 0,
  };
  const expensesByCategory    = data?.expensesByCategory    || [];
  const otherIncomeByCategory = data?.otherIncomeByCategory || [];
  const passThrough           = data?.passThrough || {
    tax: 0, deliveryFees: 0, serviceChargeCustomerPaid: 0, tipAmount: 0, total: 0,
  };

  /* Pretty label for synthetic / well-known expense categories. The
     backend returns PLATFORM_FEES as a synthetic row to balance the
     merchant-paid service charge; everything else is whatever the
     merchant typed into Expenses. */
  const expenseLabel = (cat) => {
    if (cat === 'PLATFORM_FEES') return 'Platform service charge';
    return String(cat).replace(/_/g, ' ');
  };

  const periodLabel = labelForPeriod({ mode, year, month, quarter, customFrom, customTo });

  // Available year options — 5 years back through next year for forward planning.
  const yearOptions = useMemo(() => {
    const opts = [];
    for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y -= 1) opts.push(y);
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = () => {
    if (loading) {
      toast.warning('Wait for the P&L to finish loading first');
      return;
    }
    try {
      const doc = buildPnlPdf({
        store,
        periodLabel,
        kpis,
        passThrough,
        expensesByCategory,
        otherIncomeByCategory,
      });
      const slug = (store?.name || 'pnl').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      doc.save(`${slug}-pnl-${range.from}-to-${range.to}.pdf`);
      toast.success('P&L downloaded');
    } catch (err) {
      console.error('[PNL PDF] generate failed:', err);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="fin-rep">
      <div className="fin-rep-toolbar">
        <ModeSwitcher value={mode} onChange={setMode} />
        <button
          type="button"
          className="fin-rep-btn fin-rep-btn--primary"
          onClick={handleDownload}
          disabled={loading || !!error}
        >
          <Download size={14} /> Download PDF
        </button>
      </div>

      {/* Picker row — shape changes per mode. */}
      <div className="fin-rep-pickers">
        {mode === 'month' && (
          <>
            <label className="fin-rep-picker">
              <span>Month</span>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTH_NAMES.map((n, idx) => (
                  <option key={n} value={idx}>{n}</option>
                ))}
              </select>
            </label>
            <label className="fin-rep-picker">
              <span>Year</span>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </>
        )}
        {mode === 'quarter' && (
          <>
            <label className="fin-rep-picker">
              <span>Quarter</span>
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                <option value={1}>Q1 (Jan–Mar)</option>
                <option value={2}>Q2 (Apr–Jun)</option>
                <option value={3}>Q3 (Jul–Sep)</option>
                <option value={4}>Q4 (Oct–Dec)</option>
              </select>
            </label>
            <label className="fin-rep-picker">
              <span>Year</span>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </>
        )}
        {mode === 'year' && (
          <label className="fin-rep-picker">
            <span>Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        )}
        {mode === 'custom' && (
          <>
            <label className="fin-rep-picker">
              <span>From</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                max={customTo || undefined}
              />
            </label>
            <label className="fin-rep-picker">
              <span>To</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                min={customFrom || undefined}
                max={toIsoDate(new Date())}
              />
            </label>
          </>
        )}
      </div>

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      <article className="fin-rep-pnl">
        <header className="fin-rep-pnl-head">
          <div>
            <FileText size={16} className="fin-rep-pnl-icon" />
            <h2 className="fin-rep-pnl-title">Profit &amp; Loss</h2>
          </div>
          <p className="fin-rep-pnl-period">{periodLabel}</p>
        </header>

        {loading && <PnlSkeleton />}

        {!loading && !error && (
          <>
            <PnlSectionHead>Income</PnlSectionHead>
            <PnlRow label="Sales revenue" amount={kpis.revenue} variant="line" />
            {kpis.otherIncome > 0 && (
              <>
                <PnlRow label="Other income" amount={kpis.otherIncome} variant="line" />
                {otherIncomeByCategory.map((c) => (
                  <PnlRow
                    key={c.category}
                    label={c.category.replace(/_/g, ' ')}
                    amount={c.amount}
                    variant="subline"
                  />
                ))}
              </>
            )}
            <PnlRow label="Total income" amount={kpis.totalIncome} variant="total" />

            {passThrough.total > 0 && (
              <>
                <PnlSectionHead>Pass-through (not your revenue)</PnlSectionHead>
                {passThrough.tax > 0 && (
                  <PnlRow label="Tax collected" amount={passThrough.tax} variant="line" sub="Remit to government" />
                )}
                {passThrough.deliveryFees > 0 && (
                  <PnlRow label="Delivery fees collected" amount={passThrough.deliveryFees} variant="line" sub="Passed to courier" />
                )}
                {passThrough.serviceChargeCustomerPaid > 0 && (
                  <PnlRow label="Service charge (customer-paid)" amount={passThrough.serviceChargeCustomerPaid} variant="line" sub="Platform fee, not yours" />
                )}
                {passThrough.tipAmount > 0 && (
                  <PnlRow label="Tips" amount={passThrough.tipAmount} variant="line" sub="Belongs to staff / kept separate" />
                )}
                <PnlRow label="Total pass-through" amount={passThrough.total} variant="total" />
              </>
            )}

            <PnlSectionHead>Cost of goods sold</PnlSectionHead>
            <PnlRow label="COGS" amount={kpis.cogs} variant="cogs" />

            <PnlRow label="Gross profit" amount={kpis.grossProfit} variant="total" sub={`${fmtPct(kpis.grossMargin)} margin`} />

            <PnlSectionHead>Operating expenses</PnlSectionHead>
            {expensesByCategory.length === 0 && (
              <PnlRow label="No expenses logged in this period" amount={0} variant="muted" />
            )}
            {expensesByCategory.map((c) => (
              <PnlRow
                key={c.category}
                label={expenseLabel(c.category)}
                amount={c.amount}
                variant="expense"
                sub={c.synthetic ? 'Deducted from your payouts' : undefined}
              />
            ))}
            <PnlRow label="Total expenses" amount={kpis.expenses} variant="total-expense" />

            <div className="fin-rep-pnl-net">
              <div>
                <p className="fin-rep-pnl-net-label">Net profit</p>
                <p className="fin-rep-pnl-net-sub">{fmtPct(kpis.netMargin)} of total income</p>
              </div>
              <p
                className={`fin-rep-pnl-net-amount ${kpis.netProfit >= 0 ? 'is-positive' : 'is-negative'}`}
              >
                {fmtMoney(kpis.netProfit)}
              </p>
            </div>
          </>
        )}
      </article>
    </div>
  );
}
